import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getApiErrorCode, getApiErrorStatus } from '@/lib/api/error';
import { useCorrectMerchantReceiptMutation } from '../hooks/useCorrectMerchantReceiptMutation';
import type { MerchantReceiptCorrectInput } from '../service/types';
import { centavosToPesoInput, isValidQuantityInput, parsePesoInputToCentavos } from '../utils/corrections';

export interface CorrectReceiptDialogDefaultItem {
  line_no: number;
  name: string;
  qty: string;
  line_total_centavos: string | null;
}

interface CorrectReceiptDialogProps {
  transactionId: string;
  trigger: ReactNode;
  defaultStoreName?: string | null;
  defaultTotalCentavos?: string | null;
  defaultItems?: CorrectReceiptDialogDefaultItem[];
}

const correctionSchema = z
  .object({
    store_name: z.string(),
    total: z.string(),
    items: z.array(
      z.object({
        line_no: z.number(),
        name: z.string(),
        qty: z.string(),
        line_total: z.string(),
      }),
    ),
  })
  .superRefine((values, ctx) => {
    const totalRaw = values.total.trim();
    let totalCentavos: number | null = null;
    if (totalRaw) {
      totalCentavos = parsePesoInputToCentavos(totalRaw);
      if (totalCentavos === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['total'],
          message: 'receipts.correct.errors.total_invalid',
        });
      }
    }

    let itemsSum = 0;
    values.items.forEach((item, index) => {
      const isActive = item.name.trim() !== '' || item.line_total.trim() !== '';
      if (!isActive) {
        return;
      }

      if (!item.name.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['items', index, 'name'],
          message: 'receipts.correct.errors.name_required',
        });
      }

      if (!isValidQuantityInput(item.qty) || Number(item.qty) <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['items', index, 'qty'],
          message: 'receipts.correct.errors.qty_invalid',
        });
      }

      const lineCentavos = parsePesoInputToCentavos(item.line_total);
      if (lineCentavos === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['items', index, 'line_total'],
          message: 'receipts.correct.errors.line_total_invalid',
        });
      } else {
        itemsSum += lineCentavos;
      }
    });

    const totalUsable = totalCentavos !== null && totalCentavos > 0;
    if (!totalUsable && itemsSum <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['total'],
        message: 'receipts.correct.errors.total_required',
      });
    }
  });

type CorrectionFormValues = z.infer<typeof correctionSchema>;

function buildDefaultValues(props: CorrectReceiptDialogProps): CorrectionFormValues {
  return {
    store_name: props.defaultStoreName ?? '',
    total: centavosToPesoInput(props.defaultTotalCentavos ?? null),
    items: (props.defaultItems ?? []).map((item) => ({
      line_no: item.line_no,
      name: item.name,
      qty: item.qty,
      line_total: centavosToPesoInput(item.line_total_centavos),
    })),
  };
}

function buildPayload(values: CorrectionFormValues): MerchantReceiptCorrectInput {
  const payload: MerchantReceiptCorrectInput = {};

  const storeName = values.store_name.trim();
  if (storeName) {
    payload.store_name = storeName;
  }

  const totalRaw = values.total.trim();
  if (totalRaw) {
    const totalCentavos = parsePesoInputToCentavos(totalRaw);
    if (totalCentavos !== null) {
      payload.total_centavos = totalCentavos;
    }
  }

  const items = values.items
    .filter((item) => item.name.trim() !== '' || item.line_total.trim() !== '')
    .map((item) => ({
      line_no: item.line_no,
      name: item.name.trim(),
      qty: Number(item.qty),
      line_total_centavos: parsePesoInputToCentavos(item.line_total) ?? 0,
    }));
  if (items.length) {
    payload.items = items;
  }

  return payload;
}

function mapCorrectionError(error: unknown): string {
  const status = getApiErrorStatus(error);
  const code = getApiErrorCode(error);

  if (status === 409 || code === 'RECEIPT_NOT_CORRECTABLE') {
    return 'receipts.correct.errors.not_correctable';
  }
  if (status === 422 || code === 'EMPTY_CORRECTION') {
    return 'receipts.correct.errors.empty';
  }
  if (status === 403) {
    return 'receipts.correct.errors.forbidden';
  }
  return 'receipts.correct.errors.generic';
}

export function CorrectReceiptDialog(props: CorrectReceiptDialogProps) {
  const { transactionId, trigger } = props;
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const correctMutation = useCorrectMerchantReceiptMutation(transactionId);

  const form = useForm<CorrectionFormValues>({
    resolver: zodResolver(correctionSchema),
    defaultValues: buildDefaultValues(props),
  });
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'items' });

  useEffect(() => {
    if (!open) {
      return;
    }
    setFormError(null);
    form.reset(buildDefaultValues(props));
    // props are read at open time to seed the form; intentionally scoped to `open`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const totalError = form.formState.errors.total;
  const isSubmitting = form.formState.isSubmitting || correctMutation.isPending;

  async function onSubmit(values: CorrectionFormValues) {
    setFormError(null);
    try {
      await correctMutation.mutateAsync(buildPayload(values));
      toast.success(t('receipts.correct.success'));
      setOpen(false);
    } catch (error) {
      setFormError(t(mapCorrectionError(error)));
    }
  }

  function addItem() {
    const nextLineNo =
      fields.reduce((max, field) => Math.max(max, field.line_no), 0) + 1 || fields.length + 1;
    append({ line_no: nextLineNo, name: '', qty: '1', line_total: '' });
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
          <DialogHeader>
            <DialogTitle>{t('receipts.correct.title')}</DialogTitle>
            <DialogDescription>{t('receipts.correct.description')}</DialogDescription>
          </DialogHeader>

          <div className="max-h-[60vh] space-y-4 overflow-y-auto py-4">
            <div className="space-y-1.5">
              <Label htmlFor="correct-store-name">{t('receipts.correct.store_name_label')}</Label>
              <Input
                id="correct-store-name"
                placeholder={t('receipts.correct.store_name_placeholder')}
                {...form.register('store_name')}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="correct-total">{t('receipts.correct.total_label')}</Label>
              <Input
                id="correct-total"
                inputMode="decimal"
                placeholder={t('receipts.correct.total_placeholder')}
                aria-invalid={totalError ? 'true' : 'false'}
                aria-describedby={totalError ? 'correct-total-error' : 'correct-total-help'}
                {...form.register('total')}
              />
              {totalError?.message ? (
                <p id="correct-total-error" className="text-destructive text-xs" role="alert">
                  {t(totalError.message)}
                </p>
              ) : (
                <p id="correct-total-help" className="text-muted-foreground text-xs">
                  {t('receipts.correct.total_help')}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{t('receipts.correct.items_label')}</Label>
                <Button type="button" variant="outline" size="sm" onClick={addItem}>
                  <Plus className="size-3.5" />
                  {t('receipts.correct.add_item')}
                </Button>
              </div>
              <p className="text-muted-foreground text-xs">{t('receipts.correct.items_help')}</p>

              {fields.map((field, index) => {
                const itemErrors = form.formState.errors.items?.[index];
                return (
                  <div key={field.id} className="rounded-md border p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground text-xs font-medium">
                        {t('receipts.correct.line_label', { line: field.line_no })}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label={t('receipts.correct.remove_item')}
                        onClick={() => remove(index)}
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </div>
                    <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_5rem_7rem]">
                      <div className="space-y-1">
                        <Label htmlFor={`item-name-${index}`} className="text-xs">
                          {t('receipts.correct.item_name_label')}
                        </Label>
                        <Input
                          id={`item-name-${index}`}
                          placeholder={t('receipts.correct.item_name_placeholder')}
                          aria-invalid={itemErrors?.name ? 'true' : 'false'}
                          {...form.register(`items.${index}.name` as const)}
                        />
                        {itemErrors?.name?.message ? (
                          <p className="text-destructive text-xs" role="alert">
                            {t(itemErrors.name.message)}
                          </p>
                        ) : null}
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor={`item-qty-${index}`} className="text-xs">
                          {t('receipts.correct.item_qty_label')}
                        </Label>
                        <Input
                          id={`item-qty-${index}`}
                          inputMode="decimal"
                          aria-invalid={itemErrors?.qty ? 'true' : 'false'}
                          {...form.register(`items.${index}.qty` as const)}
                        />
                        {itemErrors?.qty?.message ? (
                          <p className="text-destructive text-xs" role="alert">
                            {t(itemErrors.qty.message)}
                          </p>
                        ) : null}
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor={`item-total-${index}`} className="text-xs">
                          {t('receipts.correct.item_total_label')}
                        </Label>
                        <Input
                          id={`item-total-${index}`}
                          inputMode="decimal"
                          placeholder={t('receipts.correct.total_placeholder')}
                          aria-invalid={itemErrors?.line_total ? 'true' : 'false'}
                          {...form.register(`items.${index}.line_total` as const)}
                        />
                        {itemErrors?.line_total?.message ? (
                          <p className="text-destructive text-xs" role="alert">
                            {t(itemErrors.line_total.message)}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {formError ? (
              <p className="text-destructive text-sm" role="alert" aria-live="assertive">
                {formError}
              </p>
            ) : null}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('receipts.correct.submitting') : t('receipts.correct.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
